import { LinearProgress } from "@material-ui/core";
import { get } from "lodash";
import React from "react";
import {
	BooleanInput,
	maxLength,
	NumberInput,
	required,
	SelectInput,
	TextInput,
	useQueryWithStore,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import CodeInput from "../../components/inputs/CodeInput";
import RaInput from "../../components/inputs/RaInput";
import * as Validation from "../../utils/validation";
import syncStatuses from "../syncStatuses";

const optionalUrl = () => (value) => {
	if (!value || value.length === 0) return undefined;
	let valid = Validation.url(value);
	return valid === true ? undefined : valid;
};

const optionalJson = () => (value) => {
	let valid = Validation.json(value || "{}");
	return valid === true ? undefined : valid;
};

const AdvancedInput = ({ create, ...props }) => {
	const formState = useFormState();
	const form = useForm();
	const platformEnabled = get(formState.values, "bookmaker_platform_id");
	const translate = useTranslate();
	const { loaded, data: bookmakerPlatforms } = useQueryWithStore({
		type: "getList",
		resource: "bookmaker-platforms",
		payload: {
			sort: { field: "order", order: "ASC" },
			pagination: {
				page: 1,
				perPage: 200,
			},
		},
	});
	const handleChange = (e) => {
		const { value } = e.target;
		const platform = bookmakerPlatforms.find((bp) => bp.id === value);
		form.batch(() => {
			if (create) {
				form.change("sync_config", get(platform, "sync_config_template", "{}"));
				form.change(
					"sync_channel_config",
					get(platform, "sync_channel_config_template", "{}")
				);
			}
		});
	};
	if (!loaded) {
		return (
			<RaInput>
				<LinearProgress />
			</RaInput>
		);
	}
	return (
		<span>
			<RaInput>
				<SelectInput
					{...props}
					source="sync_status"
					choices={syncStatuses}
					validate={required()}
				/>
			</RaInput>
			<RaInput>
				<SelectInput
					{...props}
					source="bookmaker_platform_id"
					choices={bookmakerPlatforms.map((bp) => ({
						id: bp.id,
						name: bp.name,
					}))}
					validate={required()}
					onChange={handleChange}
				/>
			</RaInput>
			{platformEnabled && (
				<React.Fragment>
					<RaInput>
						<TextInput
							{...props}
							source="sync_base_url"
							validate={[optionalUrl(), maxLength(200)]}
							fullWidth
							className=""
						/>
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_stats_url"
							validate={[optionalUrl(), maxLength(1000)]}
							fullWidth
							className=""
						/>
					</RaInput>
					<RaInput>
						<BooleanInput {...props} source="sync_import_today" />
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_times"
							helperText={translate(
								"resources.bookmakers.fields.sync_times.help"
							)}
							fullWidth
							className=""
							defaultValue="15:00,17:00,19:00"
						/>
					</RaInput>
					<RaInput>
						<NumberInput
							{...props}
							source="sync_days_quantity"
							defaultValue={6}
						/>
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_date_format"
							validate={maxLength(50)}
						/>
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_product_name"
							fullWidth
							className=""
							validate={maxLength(50)}
						/>
					</RaInput>
					<RaInput>
						<NumberInput source="sync_cpa_quantity" {...props} />
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_reports_url"
							validate={[optionalUrl(), maxLength(1000)]}
							fullWidth
							className=""
						/>
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_username"
							validate={[maxLength(50)]}
						/>
					</RaInput>
					<RaInput>
						<TextInput
							{...props}
							source="sync_password"
							type="password"
							validate={[maxLength(50)]}
						/>
					</RaInput>
					<RaInput>
						<CodeInput
							{...props}
							source="sync_config"
							type="json"
							fullWidth
							className=""
							validate={[required(), optionalJson()]}
							defaultValue={"{}"}
						/>
					</RaInput>
					<RaInput>
						<CodeInput
							source="sync_config_customers"
							type="json"
							fullWidth
							className=""
							validate={[optionalJson()]}
							defaultValue={"{}"}
							{...props}
						/>
					</RaInput>
					<RaInput>
						<CodeInput
							source="sync_config_dynamic_variables"
							type="json"
							fullWidth
							className=""
							validate={[optionalJson()]}
							defaultValue={"{}"}
							{...props}
						/>
					</RaInput>
					<RaInput>
						<CodeInput
							source="sync_settings"
							type="json"
							fullWidth
							className=""
							validate={[optionalJson()]}
							defaultValue={"{}"}
							{...props}
						/>
					</RaInput>
				</React.Fragment>
			)}
		</span>
	);
};

export default AdvancedInput;
